import React, { useState, useCallback, useRef, useMemo } from 'react';
import { cn } from '../primitives';
import { SCORE_COLORS, SCORE_VALUES } from './IconPicker';

/**
 * Reverse-lookup a hex color in the SCORE_COLORS picker palette and return the
 * corresponding score (0–100), or null when the color is outside the palette.
 * The picker is constrained to these 9 colors, so user-picked label colors
 * round-trip cleanly through this map.
 */
function colorToScore(color: string | null | undefined): number | null {
  if (!color) return null;
  const lower = color.toLowerCase();
  for (const score of SCORE_VALUES) {
    const palette = SCORE_COLORS[score];
    if (palette && palette.toLowerCase() === lower) return score;
  }
  return null;
}

export interface ScoreSliderLabel {
  value: number;
  name: string | null;
  color?: string | null;
}

// Default boundary color: the bright palette green at score=100 (matches the
// picker palette in the form so colorToScore can round-trip cleanly).
const DEFAULT_LABEL_COLOR = SCORE_COLORS[100] || '#00ff00';

export interface ScoreSliderProps {
  /** Field ID for identification */
  fieldId: string;
  /** Icon URL */
  icon?: string | null;
  /** Field name */
  name: string;
  /** Minimum value */
  min: number;
  /** Maximum value */
  max: number;
  /** Step increment */
  step: number;
  /** Current value (null means not selected) */
  currentValue: number | null;
  /** Units display (optional) */
  units?: string | null;
  /** Field score (0-100) that determines the color (0=red, 100=green) */
  score?: number;
  /** Labels matched against the current value */
  labels?: ScoreSliderLabel[];
  /** Callback when value changes during drag */
  onValueChange?: (value: number) => void;
  /** Callback when user releases the slider */
  onRelease?: (value: number) => void;
  /** Whether the slider is disabled */
  disabled?: boolean;
  /** Custom class name for web styling */
  className?: string;
  /** Render function for custom slider implementation (for React Native) */
  renderSlider?: (props: SliderRenderProps) => React.ReactNode;
}

export interface SliderRenderProps {
  value: number | null;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  onRelease: () => void;
  disabled?: boolean;
}

/**
 * Color mapping by score (0-100), softer version of Rails COLOR_BY_SCORE
 * 0 = red (bad), 100 = green (good)
 * Using more muted, eye-friendly colors
 */
const COLOR_BY_SCORE: [number, string][] = [
  [0, '#dc2626'],   // red-600
  [12, '#ea580c'],  // orange-600
  [25, '#d97706'],  // amber-600
  [37, '#ca8a04'],  // yellow-600
  [50, '#a3a323'],  // yellow-olive
  [62, '#65a30d'],  // lime-600
  [75, '#4d7c0f'],  // lime-700
  [87, '#15803d'],  // green-700
  [100, '#16a34a'], // green-600
];

/**
 * Parse a hex color to RGB values
 */
function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

/**
 * Get color for a given score (0-100) by interpolating between COLOR_BY_SCORE stops
 */
function getColorForScore(score: number): string {
  // Clamp score to 0-100
  const s = Math.max(0, Math.min(100, score));

  // Find the two stops to interpolate between
  let lowerIdx = 0;
  for (let i = 0; i < COLOR_BY_SCORE.length - 1; i++) {
    const nextStop = COLOR_BY_SCORE[i + 1];
    if (nextStop && nextStop[0] > s) {
      lowerIdx = i;
      break;
    }
    lowerIdx = i;
  }

  const lowerStop = COLOR_BY_SCORE[lowerIdx]!;
  const upperStop = COLOR_BY_SCORE[Math.min(lowerIdx + 1, COLOR_BY_SCORE.length - 1)]!;
  const [lowerScore, lowerColor] = lowerStop;
  const [upperScore, upperColor] = upperStop;

  // If exactly at a stop, return that color
  if (lowerScore === upperScore || s <= lowerScore) {
    return lowerColor;
  }
  if (s >= upperScore) {
    return upperColor;
  }

  // Interpolate between the two colors
  const t = (s - lowerScore) / (upperScore - lowerScore);
  const [r1, g1, b1] = parseHex(lowerColor);
  const [r2, g2, b2] = parseHex(upperColor);

  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);

  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Calculate tick marks for a slider based on its range
 * Returns an array of values where ticks should appear
 * Rules:
 * - Between 5 and 10 ticks
 * - No ticks if range is too small to meaningfully divide
 * - Interval based on range magnitude (10, 100, 1000, etc.)
 */
function calculateTicks(min: number, max: number): number[] {
  const range = max - min;

  // Don't show ticks if range is too small
  if (range < 5) return [];

  // Find appropriate interval to get 5-10 ticks
  const intervals = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000, 10000];

  let bestInterval = 1;
  for (const interval of intervals) {
    const tickCount = Math.floor(range / interval);
    if (tickCount >= 5 && tickCount <= 10) {
      bestInterval = interval;
      break;
    }
    if (tickCount < 5) {
      // Previous interval was better, but if we haven't found one with 5+ ticks yet,
      // use this one if it gives at least 2 ticks
      if (tickCount >= 2) {
        bestInterval = interval;
      }
      break;
    }
    bestInterval = interval;
  }

  // Generate tick values
  const ticks: number[] = [];
  const startTick = Math.ceil(min / bestInterval) * bestInterval;

  for (let tick = startTick; tick <= max; tick += bestInterval) {
    // Don't include min or max as ticks (they're already labeled)
    if (tick > min && tick < max) {
      ticks.push(tick);
    }
  }

  // Return empty if we don't have enough meaningful ticks
  if (ticks.length < 2) return [];

  // Limit to max 10 ticks
  if (ticks.length > 10) {
    return ticks.filter((_, i) => i % Math.ceil(ticks.length / 10) === 0).slice(0, 10);
  }

  return ticks;
}

/**
 * ScoreSlider - A cross-platform slider component for numeric field values
 *
 * For web: Uses native HTML range input with custom styling
 * For React Native: Pass a custom renderSlider prop with your native slider
 */
export function ScoreSlider({
  fieldId,
  icon,
  name,
  min,
  max,
  step,
  currentValue,
  units,
  score = 50,
  labels,
  onValueChange,
  onRelease,
  disabled = false,
  className,
  renderSlider,
}: ScoreSliderProps) {
  // null means not selected yet
  const [value, setValue] = useState<number | null>(currentValue);
  const [isDragging, setIsDragging] = useState(false);

  // Touch intent detection state
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const isIntentionalDragRef = useRef(false);
  const pendingValueRef = useRef<number | null>(null); // Store value during touch until intent is determined
  const inputRef = useRef<HTMLInputElement>(null);
  const TOUCH_THRESHOLD = 10; // pixels to determine scroll vs slider intent

  // Calculate tick marks for this slider (visual only, no snapping)
  const ticks = useMemo(() => calculateTicks(min, max), [min, max]);

  // For display purposes when value is null, show middle of range
  const displayValue = value ?? min;
  const isSelected = value !== null;

  const handleChange = useCallback((newValue: number) => {
    // Mouse interaction or intentional touch drag - apply immediately
    if (isIntentionalDragRef.current || !touchStartRef.current) {
      setValue(newValue);
      onValueChange?.(newValue);
    } else {
      // Touch in progress but intent not yet determined - store as pending
      // This will be applied if it turns out to be a tap or horizontal drag
      pendingValueRef.current = newValue;
    }
  }, [onValueChange]);

  const handleRelease = useCallback(() => {
    setIsDragging(false);
    touchStartRef.current = null;
    isIntentionalDragRef.current = false;
    pendingValueRef.current = null;
    if (value !== null) {
      onRelease?.(value);
    }
  }, [onRelease, value]);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    isIntentionalDragRef.current = false;

    // Calculate the value from touch position on the track
    // This is needed because mobile browsers don't fire onChange on track tap
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const relativeX = touch.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, relativeX / rect.width));
      const rawValue = min + percentage * (max - min);
      // Snap to step
      const snappedValue = Math.round(rawValue / step) * step;
      const clampedValue = Math.max(min, Math.min(max, snappedValue));
      pendingValueRef.current = clampedValue;
    } else {
      pendingValueRef.current = null;
    }
    // Don't set isDragging yet - wait for intent detection
  }, [min, max, step]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;

    // Already determined to be intentional, let it proceed
    if (isIntentionalDragRef.current) return;

    const touch = e.touches[0];
    if (!touch) return;
    const dx = Math.abs(touch.clientX - touchStartRef.current.x);
    const dy = Math.abs(touch.clientY - touchStartRef.current.y);

    // First significant movement determines intent
    if (dx > TOUCH_THRESHOLD || dy > TOUCH_THRESHOLD) {
      if (dx > dy) {
        // Horizontal movement = slider intent
        isIntentionalDragRef.current = true;
        setIsDragging(true);
        // Apply any pending value now that we know it's intentional
        if (pendingValueRef.current !== null) {
          setValue(pendingValueRef.current);
          onValueChange?.(pendingValueRef.current);
          pendingValueRef.current = null;
        }
      } else {
        // Vertical movement = scroll intent, cancel slider interaction
        touchStartRef.current = null;
        isIntentionalDragRef.current = false;
        pendingValueRef.current = null; // Discard pending value
      }
    }
  }, [onValueChange]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    // If it was an intentional horizontal drag, trigger release
    if (isIntentionalDragRef.current) {
      handleRelease();
      return;
    }

    // If touchStart was cancelled (vertical scroll detected), do nothing
    if (!touchStartRef.current) {
      return;
    }

    // Check if this was a tap (minimal movement) - allow taps to set value
    const touch = e.changedTouches[0];
    if (touch && touchStartRef.current) {
      const dx = Math.abs(touch.clientX - touchStartRef.current.x);
      const dy = Math.abs(touch.clientY - touchStartRef.current.y);

      // If movement was minimal, treat it as a tap and apply the pending value
      if (dx < TOUCH_THRESHOLD && dy < TOUCH_THRESHOLD && pendingValueRef.current !== null) {
        const tapValue = pendingValueRef.current;
        setValue(tapValue);
        onRelease?.(tapValue);
      }
    }

    // Clean up
    touchStartRef.current = null;
    isIntentionalDragRef.current = false;
    pendingValueRef.current = null;
    setIsDragging(false);
  }, [handleRelease, onRelease]);

  // Calculate percentage for the progress bar (0-100)
  const percentage = isSelected ? ((displayValue - min) / (max - min)) * 100 : 0;

  // Build the list of color stops used to colour the slider.
  // - First and last labels default to green when their color is null.
  // - Mid labels with null color are skipped as stops so the colour interpolates
  //   proportionally between the surrounding stops (like the mood smiley spectrum).
  const resolvedLabels = useMemo(() => {
    if (!labels?.length) return [];
    const sorted = [...labels].sort((a, b) => a.value - b.value);
    return sorted
      .map((label, idx) => {
        const isBoundary = idx === 0 || idx === sorted.length - 1;
        const color = label.color || (isBoundary ? DEFAULT_LABEL_COLOR : null);
        return color ? { value: label.value, color } : null;
      })
      .filter((stop): stop is { value: number; color: string } => stop !== null);
  }, [labels]);

  const colorForValue = useCallback((targetValue: number): string => {
    if (resolvedLabels.length === 0) return getColorForScore(score);
    if (resolvedLabels.length === 1) return resolvedLabels[0]!.color;

    // Render any palette-picker color through the muted spectrum so the slider
    // uses darker, more legible greens/reds instead of the bright picker hexes.
    const renderColor = (color: string): string => {
      const s = colorToScore(color);
      return s !== null ? getColorForScore(s) : color;
    };

    if (targetValue <= resolvedLabels[0]!.value) return renderColor(resolvedLabels[0]!.color);
    const last = resolvedLabels[resolvedLabels.length - 1]!;
    if (targetValue >= last.value) return renderColor(last.color);

    for (let i = 0; i < resolvedLabels.length - 1; i++) {
      const a = resolvedLabels[i]!;
      const b = resolvedLabels[i + 1]!;
      if (targetValue >= a.value && targetValue <= b.value) {
        if (a.value === b.value) return a.color;
        const t = (targetValue - a.value) / (b.value - a.value);

        // Preferred path: both endpoints map to the score-color palette, so we
        // walk along the natural red→yellow→green spectrum used elsewhere in
        // the app rather than mixing RGB channels directly (which produces
        // muddy olive/brown midtones).
        const aScore = colorToScore(a.color);
        const bScore = colorToScore(b.color);
        if (aScore !== null && bScore !== null) {
          const interpolatedScore = aScore + (bScore - aScore) * t;
          return getColorForScore(interpolatedScore);
        }

        // Fallback: straight RGB blend when colors fall outside the palette.
        const [r1, g1, bb1] = parseHex(a.color);
        const [r2, g2, bb2] = parseHex(b.color);
        const r = Math.round(r1 + (r2 - r1) * t);
        const g = Math.round(g1 + (g2 - g1) * t);
        const bl = Math.round(bb1 + (bb2 - bb1) * t);
        return `rgb(${r}, ${g}, ${bl})`;
      }
    }
    return last.color;
  }, [resolvedLabels, score]);

  // Get color based on the current value, interpolated between label colors.
  // Gray when not selected.
  const unselectedColor = '#9ca3af';
  const fillColor = isSelected ? colorForValue(displayValue) : unselectedColor;

  // Format units display - convert unit types to symbols
  const formatUnits = (unit: string | null | undefined): string => {
    if (!unit || unit === 'none') return '';
    switch (unit) {
      case 'percentage': return '%';
      case 'duration': return 'min';
      case 'volume': return 'ml';
      case 'steps': return 'steps';
      default: return unit;
    }
  };
  const unitsDisplay = formatUnits(units);

  // Find label whose value matches the current value (only when selected)
  const matchedLabel = isSelected && labels?.length
    ? labels.find((l) => l.value === value)
    : undefined;

  const sliderRenderProps: SliderRenderProps = {
    value,
    min,
    max,
    step,
    onChange: handleChange,
    onRelease: handleRelease,
    disabled,
  };

  return (
    <div
      className={cn(
        'score-slider',
        !isSelected && 'score-slider--unselected',
        isDragging && 'score-slider--dragging',
        className
      )}
      data-field-id={fieldId}
    >
      {/* Compact inline layout: icon | slider */}
      <div className="score-slider__row">
        {/* Icon */}
        {icon && (
          <span
            className="score-slider__icon"
            style={{
              backgroundImage: `url(${icon})`,
              filter: isSelected ? 'none' : 'grayscale(100%) opacity(0.5)',
            }}
            title={name}
          />
        )}

        {/* Slider with value bubble */}
        <div className="score-slider__slider-area">
          {/* Field name - compact */}
          <span className="score-slider__name">
            {name}
            {matchedLabel && (
              <span
                className="score-slider__matched-label"
                style={{
                  marginLeft: '0.5rem',
                  padding: '0.1rem 0.45rem',
                  borderRadius: '0.5rem',
                  backgroundColor: fillColor,
                  color: '#fff',
                  fontSize: '0.75em',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                {matchedLabel.name}
              </span>
            )}
          </span>

          {/* Custom slider or default web implementation */}
          {renderSlider ? (
            renderSlider(sliderRenderProps)
          ) : (
            <div className="score-slider__track-wrapper">
              {/* Progress bar */}
              <div className="score-slider__track">
                <div
                  className="score-slider__progress"
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: fillColor,
                  }}
                />
                {/* Tick marks */}
                {ticks.map((tick) => {
                  const tickPercentage = ((tick - min) / (max - min)) * 100;
                  return (
                    <div
                      key={tick}
                      className="score-slider__tick"
                      style={{ left: `${tickPercentage}%` }}
                    />
                  );
                })}
              </div>

              {/* Native range input */}
              <input
                ref={inputRef}
                type="range"
                min={min}
                max={max}
                step={step}
                value={displayValue}
                onChange={(e) => handleChange(Number(e.target.value))}
                onMouseDown={handleMouseDown}
                onMouseUp={handleRelease}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                disabled={disabled}
                className="score-slider__input"
                aria-label={name}
              />

              {/* Thumb with value bubble */}
              <div
                className={cn(
                  'score-slider__thumb',
                  !isSelected && 'score-slider__thumb--unselected'
                )}
                style={{
                  left: `${percentage}%`,
                  borderColor: isSelected ? fillColor : unselectedColor,
                }}
              >
                {/* Value bubble - show placeholder when unselected */}
                <div
                  className={cn(
                    'score-slider__bubble',
                    isDragging && 'score-slider__bubble--active',
                    !isSelected && 'score-slider__bubble--placeholder'
                  )}
                  style={isSelected ? {
                    backgroundColor: fillColor,
                    '--bubble-arrow-color': fillColor,
                  } as React.CSSProperties : undefined}
                >
                  {isSelected ? (
                    <>{displayValue}{unitsDisplay && <small>{unitsDisplay}</small>}</>
                  ) : (
                    <span className="score-slider__tap-hint">—</span>
                  )}
                </div>
              </div>

              {/* Min/Max labels */}
              <div className="score-slider__labels">
                <span className="score-slider__label">{min}</span>
                <span className="score-slider__label">{max}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
